import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plant, plantsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PlantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadPlant();
    }
  }, [id]);

  const loadPlant = async () => {
    try {
      setLoading(true);
      const response = await plantsAPI.getPlant(parseInt(id!));
      setPlant(response.plant);
    } catch (err: any) {
      setError(err.message || 'Failed to load plant details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading plant details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: '#dc3545', marginBottom: '20px' }}>Error: {error}</div>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!plant) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: '#dc3545', marginBottom: '20px' }}>Plant not found</div>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e9ecef'
      }}>
        <div>
          <h1 style={{ 
            margin: '0', 
            color: '#2c5530', 
            fontSize: '32px',
            fontWeight: 'bold'
          }}>
            {plant.primary_scientific_name}
          </h1>
          <p style={{ 
            margin: '5px 0 0 0', 
            color: '#666', 
            fontSize: '18px',
            fontStyle: 'italic'
          }}>
            {plant.family}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
          {(user?.role === 'admin' || user?.role === 'super-admin') && (
            <button 
              onClick={() => navigate(`/plants/${plant.id}/edit`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Edit Plant
            </button>
          )}
        </div>
      </div>

      {/* Plant Information Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
        {/* Main Information */}
        <div>
          {/* Basic Information */}
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '20px', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>🌿 Basic Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <strong>Scientific Name:</strong><br />
                <span style={{ fontStyle: 'italic' }}>{plant.primary_scientific_name}</span>
              </div>
              <div>
                <strong>Family:</strong><br />
                {plant.family}
              </div>
              <div>
                <strong>Genus:</strong><br />
                {plant.genus || 'N/A'}
              </div>
              <div>
                <strong>Species:</strong><br />
                {plant.species || 'N/A'}
              </div>
              <div>
                <strong>Status:</strong><br />
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: plant.status === 'published' ? '#d4edda' : '#fff3cd',
                  color: plant.status === 'published' ? '#155724' : '#856404',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {plant.status}
                </span>
              </div>
              <div>
                <strong>UUID:</strong><br />
                <code style={{ 
                  backgroundColor: '#e9ecef', 
                  padding: '2px 4px', 
                  borderRadius: '3px',
                  fontSize: '12px'
                }}>
                  {plant.species_uuid}
                </code>
              </div>
            </div>
          </div>

          {/* Description */}
          {plant.description && (
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>📝 Description</h3>
              <p style={{ lineHeight: '1.6', color: '#333' }}>{plant.description}</p>
            </div>
          )}

          {/* Detailed Description */}
          {plant.detailed_description && (
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>📖 Detailed Description</h3>
              <p style={{ lineHeight: '1.6', color: '#333' }}>{plant.detailed_description}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Classification */}
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>🏷️ Classification</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div>
                <strong>Size Category:</strong><br />
                {plant.size_category || 'N/A'}
              </div>
              <div>
                <strong>IUCN Status:</strong><br />
                {plant.iucn_status || 'N/A'}
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>📊 Metadata</h3>
            <div style={{ display: 'grid', gap: '10px', fontSize: '14px' }}>
              <div>
                <strong>Created:</strong><br />
                {new Date(plant.created_at).toLocaleDateString()}
              </div>
              <div>
                <strong>Last Updated:</strong><br />
                {new Date(plant.updated_at).toLocaleDateString()}
              </div>
              <div>
                <strong>Uploaded By:</strong><br />
                User ID: {plant.uploaded_by}
              </div>
              <div>
                <strong>View Count:</strong><br />
                {plant.view_count || 0}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#2c5530', marginBottom: '15px' }}>⚡ Actions</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              <button style={{
                padding: '10px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                📸 View Images
              </button>
              <button style={{
                padding: '10px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                📍 View Locations
              </button>
              <button style={{
                padding: '10px',
                backgroundColor: '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                📚 View Synonyms
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantDetails;

